import { baseApi } from 'shared/api/baseApi'
import { CMS_PAGES_TAG } from 'shared/api/tags'
import type {
    CMSCreateTranslationRequest,
    CMSCreatePageRequest,
    CMSGenerateUploadUrlResponse,
    CMSPage,
    CMSPageListParams,
    CMSUpdatePageRequest,
} from '../model/types'

export const cmsPagesApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCmsPages: build.query<CMSPage[], CMSPageListParams>({
            query: ({ organizationId, locale }) => ({
                url: `organizations/${organizationId}/cms-pages`,
                method: 'GET',
                params: locale ? { locale } : undefined,
            }),
            providesTags: [CMS_PAGES_TAG],
        }),

        getCmsPage: build.query<
            CMSPage,
            { organizationId: string; pageId: string }
        >({
            query: ({ organizationId, pageId }) => ({
                url: `organizations/${organizationId}/cms-pages/${pageId}`,
                method: 'GET',
            }),
            providesTags: [CMS_PAGES_TAG],
        }),

        createCmsPage: build.mutation<CMSPage, CMSCreatePageRequest>({
            query: ({ organizationId, ...body }) => ({
                url: `organizations/${organizationId}/cms-pages`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_PAGES_TAG],
        }),

        updateCmsPage: build.mutation<CMSPage, CMSUpdatePageRequest>({
            query: ({ organizationId, pageId, data }) => ({
                url: `organizations/${organizationId}/cms-pages/${pageId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: [CMS_PAGES_TAG],
        }),

        generateCmsPageUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string; pageId: string }
        >({
            query: ({ organizationId, pageId }) => ({
                url: `organizations/${organizationId}/cms-pages/${pageId}/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        // Upload before the page is saved; the key is not tied to an id.
        generateNewCmsPageUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string }
        >({
            query: ({ organizationId }) => ({
                url: `organizations/${organizationId}/cms-pages/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        getCmsPagePreviewId: build.mutation<
            { previewId: string },
            { organizationId: string; pageId: string }
        >({
            query: ({ organizationId, pageId }) => ({
                url: `organizations/${organizationId}/cms-pages/${pageId}/preview`,
                method: 'POST',
            }),
        }),

        createCmsPageTranslation: build.mutation<
            CMSPage,
            CMSCreateTranslationRequest
        >({
            query: ({ organizationId, id, locale }) => ({
                url: `organizations/${organizationId}/cms-pages/${id}/translations`,
                method: 'POST',
                body: { locale },
            }),
            invalidatesTags: [CMS_PAGES_TAG],
        }),

        publishCmsPage: build.mutation<void, { orgId: string; id: string }>({
            query: ({ orgId, id }) => ({
                url: `organizations/${orgId}/cms-pages/${id}/publish`,
                method: 'POST',
            }),
            invalidatesTags: [CMS_PAGES_TAG],
        }),
    }),
})

export const {
    useGetCmsPagesQuery,
    useGetCmsPageQuery,
    useLazyGetCmsPageQuery,
    useCreateCmsPageMutation,
    useUpdateCmsPageMutation,
    useGenerateCmsPageUploadUrlMutation,
    useGenerateNewCmsPageUploadUrlMutation,
    useGetCmsPagePreviewIdMutation,
    usePublishCmsPageMutation,
    useCreateCmsPageTranslationMutation,
} = cmsPagesApi
